import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiService from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
  const [userPermissions, setUserPermissions] = useState(() => {
    const stored = sessionStorage.getItem('userPermissions');
    return stored ? JSON.parse(stored) : [];
  });

  // Function to fetch user permissions using apiService
  const fetchUserPermissions = useCallback(async (userId) => {
    try {
      const response = await apiService.get(`/enterprise_permission/by_user/${userId}`);
      const permissions = response.data;
      setUserPermissions(permissions);
      sessionStorage.setItem('userPermissions', JSON.stringify(permissions));
      return permissions;
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      return [];
    }
  }, []);

  useEffect(() => {
    // Check if user is already authenticated on mount
    checkAuthStatus();
  }, []);

  const checkAuthStatus = () => {
    try {
      const isAuth = apiService.isAuthenticated();
      const userData = apiService.getStoredPlatformUser();

      if (isAuth && userData) {
        setUser(userData);
        setIsAuthenticated(true);
        // Check if password change is required
        if (userData.password_change_required === true) {
          setPasswordChangeRequired(true);
        } else {
          setPasswordChangeRequired(false);
        }
        // Fetch user permissions if not already in sessionStorage
        if (userData.id && !sessionStorage.getItem('userPermissions')) {
          fetchUserPermissions(userData.id);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setPasswordChangeRequired(false);
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      setUser(null);
      setIsAuthenticated(false);
      setPasswordChangeRequired(false);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await apiService.login(email, password);

      if (response.success) {
        // Check if the user is a person (required for enterprise access)
        if (response.user && response.user.is_person === false) {
          return { success: false, error: "Account Type not allowed enterprise access" };
        }

        setUser(response.user);
        setIsAuthenticated(true);

        // Check if password change is required after successful login
        if (response.user && response.user.password_change_required === true) {
          setPasswordChangeRequired(true);
        } else {
          setPasswordChangeRequired(false);
        }

        // Fetch user permissions after successful login
        if (response.user && response.user.id) {
          await fetchUserPermissions(response.user.id);
        }

        return { success: true, user: response.user };
      } else {
        return { success: false, error: response.error };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Error during logout:', error);
      // Continue with local logout even if server call fails
    }
    setUser(null);
    setIsAuthenticated(false);
    setPasswordChangeRequired(false);
    setUserPermissions([]);
    sessionStorage.removeItem('userPermissions');
  };

  const handlePasswordChanged = () => {
    // Update user data to reflect password change is no longer required
    if (user) {
      const updatedUser = { ...user, password_change_required: false };
      setUser(updatedUser);
      // Update user data in localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
    setPasswordChangeRequired(false);
  };

  const refreshToken = async () => {
    try {
      const response = await apiService.refreshToken();

      if (response.success) {
        // Token was refreshed successfully
        return { success: true };
      } else {
        // Token refresh failed, logout user
        console.warn('Token refresh failed:', response.error);
        await logout();
        return { success: false, error: response.error };
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      await logout();
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    isAuthenticated,
    loading,
    passwordChangeRequired,
    userPermissions,
    login,
    logout,
    checkAuthStatus,
    handlePasswordChanged,
    refreshToken,
    fetchUserPermissions
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;